import React, { useState } from 'react';
import { Box, Chip, Grid, Typography, Skeleton, IconButton, TextField, MenuItem, Button } from '@mui/material';
import styled from '@emotion/styled';
import SegmentedStar from './SegmentedStar';
import { Edit, Check, Close } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { updateUser } from '../redux/slices/users';
import { useHasPermission } from '../utils/permissionUtils';

const TaskContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    marginTop: theme.spacing(2),
    height: '100%',
}));

const BoxHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
}));

const BoxTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    textAlign: 'left',
    marginRight: theme.spacing(2),
}));

const DataSet = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
}));

const SkillChip = styled(Chip)(({ theme }) => ({
    boxShadow: theme.shadows[2],
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    fontSize: theme.typography.size.md,
}));

const SkillBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const SkillChipLabel = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
}));

const ProfileSkillBox = ( {skills=[], isLoading=true, userId} ) => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const [currentSkills, setCurrentSkills] = useState(skills);
    const [editedSkills, setEditedSkills] = useState(skills);
    const canEditUser = hasPermission("editUsers");
    const isSelf = userId === useSelector(state => state.auth.userId);
    const [isEditing, setIsEditing] = useState(false);

    const getSkillLevel = (level) => {
        switch (level) {
            case 'Beginner': return <SegmentedStar value={2} size={24}/>;
            case 'Intermediate': return <SegmentedStar value={3} size={24} />;
            case 'Advanced': return <SegmentedStar value={4} size={24} />;
            case 'Expert': return <SegmentedStar value={5} size={24} />;
            default: return <SegmentedStar value={2} size={24} />;
        }
    }

    const handleUserEdit = () => {
      setIsEditing(true);
      setEditedSkills(currentSkills);
    };

    const handleUserSave = () => {
      dispatch(
        updateUser({
          userId: userId,
          userData: { skills: editedSkills },
          callback: (response) => {
            if (response?.user) {
              setCurrentSkills(response.user.skills);
              setIsEditing(false);
            }
          },
        })
      );
    };

    const handleUserCancel = () => {
      setIsEditing(false);
      setEditedSkills(currentSkills);
    };

    const handleSkillChange = (index, field, value) => {
      setEditedSkills(editedSkills.map((skill, i) => i === index ? {...skill, [field]: value} : skill));
    };

    const addSkill = () => {
      setEditedSkills([...editedSkills, { skill: '', level: 'Beginner' }]);
    };

    return (
        <Grid item xs={12} md={4} lg={4}>
            <TaskContainer>
                <BoxHeader>
                    <BoxTitle variant="h5">Skills</BoxTitle>
                    {(canEditUser || isSelf) && (
                        <>
                            {isEditing ? (
                                <Box>
                                    <IconButton onClick={handleUserSave}>
                                        <Check />
                                    </IconButton>
                                    <IconButton onClick={handleUserCancel}>
                                        <Close />
                                    </IconButton>
                                </Box>
                            ) : (
                                <IconButton onClick={handleUserEdit}>
                                    <Edit />
                                </IconButton>
                            )}
                        </>
                    )}
                </BoxHeader>
                <DataSet>
                    {isLoading ? <><Skeleton width={70} height={40} /> <Skeleton width={70} height={40} /> <Skeleton width={70} height={40} /> <Skeleton width={70} height={40} /></> : (
                        isEditing ? (
                            <>
                                {editedSkills.map((item, index) => (
                                    <SkillBox key={index}>
                                        <TextField fullWidth label="Skill" value={item.skill} onChange={(e) => handleSkillChange(index, 'skill', e.target.value)} />
                                        <TextField fullWidth label="Level" value={item.level} onChange={(e) => handleSkillChange(index, 'level', e.target.value)} select>
                                            <MenuItem value="Beginner">Beginner</MenuItem>
                                            <MenuItem value="Intermediate">Intermediate</MenuItem>
                                            <MenuItem value="Advanced">Advanced</MenuItem>
                                            <MenuItem value="Expert">Expert</MenuItem>
                                        </TextField>
                                    </SkillBox>
                                ))}
                                <Button variant="outlined" onClick={addSkill}>Add Skill</Button>
                            </>
                        ) : (
                            currentSkills.map((item, index) => (
                                <SkillChip size="small" key={index} label={<SkillChipLabel>{item.skill} {getSkillLevel(item.level)}</SkillChipLabel>} />
                            ))
                        )
                    )}
                </DataSet>
            </TaskContainer>
        </Grid>
    );
};

export default ProfileSkillBox;